import { APIClient } from "../../helpers/api_helper";

const api = new APIClient();

// ─── Types ────────────────────────────────────────────────────────────────────

export type CourseAssignmentStatus = "ASSIGNED" | "REVOKED";

export interface CourseAssignment {
  _id: string;
  courseId: string;
  courseName: string;
  fullName: string | null;
  email: string | null;
  countryCode: number;
  phoneNumber: number;
  userId: string | null;
  status: CourseAssignmentStatus;
  assignedBy: string;
  assignedAt: string;
  icon: string | null;
  visibility: string;
}

export interface ListCourseAssignmentsParams {
  phoneNumber?: string;
  status?: string;
  pageNum?: number;
  pageSize?: number;
}

export interface ListCourseAssignmentsResponse {
  results: CourseAssignment[];
  totalCount: number;
}

export interface AssignCoursePayload {
  courseId: string;
  countryCode: number;
  phoneNumber: number;
  fullName: string;
}

export interface SelectOption {
  label: string;
  value: string;
}

// ─── Academy list (real API) ──────────────────────────────────────────────────

interface AcademyListItem {
  _id: string;
  basicDetails: {
    academyName: string;
    sports: string[];
  };
}

export async function listAssignmentAcademies(): Promise<SelectOption[]> {
  const response = await api.get("/api/v1/academy/admin/list", undefined);
  const resp = response as { data?: AcademyListItem[] };
  const items: AcademyListItem[] = Array.isArray(resp?.data) ? resp.data! : [];
  return items.map((a) => ({
    value: a._id,
    label: a.basicDetails.academyName,
  }));
}

// ─── Assignable courses by academy (real API, supports search) ───────────────

interface AssignableCourseItem {
  _id: string;
  courseName: string;
  icon: string | null;
  proficiency: string;
  visibility: string;
}

export async function findAssignableCourses(
  academyId: string,
  search?: string,
  pageNum = 1,
  pageSize = 20
): Promise<SelectOption[]> {
  if (!academyId) return [];
  const params: Record<string, string | number> = { academyId, pageNum, pageSize };
  if (search?.trim()) params.search = search.trim();
  const response = await api.get("/api/v1/courses/admin/assignable-courses", params);
  const data = (response as { data?: { results?: AssignableCourseItem[] } })?.data;
  const items = data?.results ?? [];
  return items.map((c) => ({ value: c._id, label: c.courseName }));
}

// ─── List assignments (real API) ──────────────────────────────────────────────

export async function listCourseAssignments(
  params: ListCourseAssignmentsParams = {}
): Promise<ListCourseAssignmentsResponse> {
  const { phoneNumber, status, pageNum = 1, pageSize = 20 } = params;

  const query: Record<string, string | number> = { pageNum, pageSize };
  if (phoneNumber?.trim()) query.phoneNumber = phoneNumber.trim();
  if (status) query.status = status;

  const body = await api.get("/api/v1/courses/admin/assignments", query);
  const data = (body as { data?: { results?: CourseAssignment[]; totalCount?: number } })?.data;

  return {
    results: data?.results ?? [],
    totalCount: data?.totalCount ?? 0,
  };
}

// ─── Create assignment (real API) ─────────────────────────────────────────────

export async function assignCourse(payload: AssignCoursePayload): Promise<void> {
  await api.create("/api/v1/courses/admin/assign-course", payload);
}

// ─── Update assignment status (real API) ──────────────────────────────────────

export async function updateAssignmentStatus(
  id: string,
  status: CourseAssignmentStatus
): Promise<void> {
  await api.create(`/api/v1/courses/admin/update-assignment-status/${id}`, { status });
}
